import InquiryColumnDefs from "../../components/Inquiries/InquiryColumnDefs.js";
import { useState, useRef, useEffect, useMemo } from "react";
import { usePermissions } from "../../hooks/usePermissions";
import { useSnackbar } from "notistack";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Button } from "@mui/material";

import Table, { Column, useTable } from "../../Monolith-UI/Table/Table.js";
import InquiryFilterDimensions from "../../components/Inquiries/InquiryFilterDimensions.js";
import InquiriesAPI from "../../api/inquiries/index.js";
import { addCustomFieldsToColumnDefs } from "../../components/Inquiries/CustomFieldComponent.js";
import { getDateFormat, monolithMoment } from "../../utils/date-format";
import { useQueryFilter } from "../../Monolith-UI/QueryFilter/QueryFilter.js";
import { Helmet } from "react-helmet-async";
import ComboButton from "../../Monolith-UI/ComboButton/ComboButton.js";

//Icons
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import ZoomOutMapOutlinedIcon from "@mui/icons-material/ZoomOutMapOutlined";
import ZoomInMapOutlinedIcon from "@mui/icons-material/ZoomInMapOutlined";
import ReorderOutlinedIcon from "@mui/icons-material/ReorderOutlined";
import styled, { useTheme } from "styled-components";
import Flyout, { FlyoutHeader } from "../../Monolith-UI/Flyout/Flyout.js";
import TaskButton from "../../Monolith-UI/TaskButton.js";
import Loader from "../../components/Loader.js";
import { nanoid } from "nanoid";
import { Link, useNavigate } from "react-router-dom";
import synchronizeColumnState from "../../utils/synchronize-column-state.js";
import { useDebounce, useDebouncedCallback } from "use-debounce";
import { ItemTotalBadge } from "@/Features";
import { SendIcon } from "lucide-react";
import { Input } from "@monolith-forensics/monolith-ui";
import AccessDeniedMessage from "../../components/AccessDeniedMessage.js";
import CustomAttribute from "@/types/CustomAttribute.js";
import {
  ColumnProps,
  PageData,
  TableColumn,
} from "@/Monolith-UI/Table/types/Table.js";
import { Typography } from "@monolith-forensics/monolith-ui";
import { ColumnResize, Condition } from "../Cases/types/Cases.js";
import CustomFieldsApi from "@/api/custom-fields/index";
import { CUSTOM_FIELD_CATEGORIES, CustomField } from "@/Features";
import { safeParseJSON } from "@/utils/File-parsers/safeParseJSON.js";
import { CreateInquiryModal, DeleteInquiryModal } from "@/Features";
import type { Inquiry } from "@/Features/Inquiries/inquiries.types";

export type { Inquiry } from "@/Features/Inquiries/inquiries.types";

interface QueryFilterRef {
  clear: () => void;
}

interface InquiryQuery {
  query: {
    conditions?: Condition[];
    page?: number;
    pageSize?: number;
    search?: string | null;
    order?: {
      field: string;
      sort: string;
    } | null;
  };
}

const defaultQueryKey = "inquiries:list";

const columnDefs = InquiryColumnDefs;

const FlyoutContent = styled(
  ({ className, defaultInfo, onDelete = () => {}, setIsFlyoutVisible }) => {
    const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
    const theme: any = useTheme();
    const [showDeleteModel, setShowDeleteModel] = useState(false);

    const { data: itemInfo } = useQuery({
      queryKey: ["inquiry", { inquiry_id: defaultInfo.inquiry_id }],
      queryFn: () =>
        InquiriesAPI.getInquiries({ inquiry_id: defaultInfo.inquiry_id }),
      initialData: [defaultInfo],
      select: (data) => {
        return data[0];
      },
    });

    const handleDelete = () => {
      setIsFlyoutVisible(false);
      onDelete(itemInfo);
    };

    const dataFieldIgnoreList = ["inquiry_notes"];

    const data = {
      ...itemInfo,
      ...(itemInfo?.custom_attributes?.reduce(
        (
          acc: { [key: string]: string | number | string[] },
          attr: CustomAttribute
        ) => {
          acc[`custom_field_${attr.field_id}`] = attr.value;
          return acc;
        },
        {}
      ) || {}),
    };

    const detail = (
      <div className="detail-container">
        {columnDefs
          .filter((c) => !dataFieldIgnoreList.includes(c.dataField))
          .filter((c) =>
            c.dataField.includes("custom_field_") ? !!data[c.dataField] : true
          )
          .map((c) => {
            return (
              <div className="detail-item" key={nanoid()}>
                <div className="detail-label">{c.caption}</div>
                {c.render ? (
                  c.render(data)
                ) : c.dataType === "date" ? (
                  <>
                    {monolithMoment({
                      timestamp: data[c.dataField],
                      includeTime: true,
                    })}
                  </>
                ) : (
                  <div className="detail-value">{data[c.dataField]}</div>
                )}
              </div>
            );
          })}
      </div>
    );

    return (
      <div className={className}>
        <div className="action-menu">
          <Link
            className={className}
            to={`/inquiries/${itemInfo.inquiry_id}`}
            style={{ padding: 0, margin: 0 }}
          >
            <div className="action-menu-item">
              <LaunchOutlinedIcon
                style={{ color: theme.palette.primary.main }}
              />
              <div className="action-menu-label">View Inquiry</div>
            </div>
          </Link>
          {hasPermission(MONOLITH_PERMISSIONS.INQUIRIES_DELETE) && (
            <>
              <div
                className="action-menu-item"
                onClick={() => setShowDeleteModel(true)}
              >
                <DeleteOutlineOutlinedIcon
                  style={{ color: theme.palette.error.main }}
                />
                <div className="action-menu-label">Delete</div>
              </div>
            </>
          )}
        </div>
        {detail}
        <div className="detail-label" style={{ marginTop: 30 }}>
          Description
        </div>
        <div style={{ position: "relative" }}>
          <div className="description">{itemInfo.inquiry_notes}</div>
        </div>
        <DeleteInquiryModal
          show={showDeleteModel}
          onClose={() => setShowDeleteModel(false)}
          onSubmit={() => handleDelete()}
          defaultInfo={itemInfo}
        />
      </div>
    );
  }
)`
  padding: 20px;

  & {
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    margin-right: 5px;
  }
  & .detail-container {
  }
  & .action-menu {
    display: flex;
    align-items: center;
    align-content: center;
    margin-bottom: 25px;
    margin-right: 10px;
  }
  & .action-menu-item {
    display: flex;
    align-items: center;
    align-content: center;
    margin-right: 12px;
    cursor: pointer;
    border-radius: 5px;
    font-size: 12px;
    &:hover {
      text-decoration: underline;
    }
    & svg {
      font-size: 15px;
      margin-right: 5px;
    }
  }
  & .detail-item {
    display: flex;
    align-items: center;
    align-content: center;
    margin: 10px 0px;
    font-size: 12px;
  }
  & .detail-label {
    color: ${({ theme }) => theme.palette.text.secondary};
    margin-right: 10px;
    min-width: 150px;
  }
  & .detail-value {
    color: ${({ theme }) => theme.palette.text.primary};
  }
  & .description {
    position: relative;
    resize: none;
    margin-top: 10px;
    font-size: 12px;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: transparent;
    white-space: pre-wrap;
    width: 100%;
    height: fit-content;
    max-height: 30%;
    border: 1px solid transparent;
    border-radius: 5px;
    padding: 10px;
    transition: border 0.2s ease-in-out;
    outline: none;
    &:hover {
      border: 1px solid ${({ theme }) => theme.palette.divider};
    }
    &:focus {
      border: 1px solid ${({ theme }) => theme.palette.primary.main};
    }
  }
  & .description-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    color: ${({ theme }) => theme.palette.divider};
    display: flex;
    alignitems: center;
    fontsize: 12px;
    padding: 10px;
  }
`;

const InquirySectionWrapper = styled(
  ({ className, stateStoreKey = "inquiries:view" }) => {
    const navigate = useNavigate();
    const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
    const { enqueueSnackbar } = useSnackbar();
    const editEvidencePopup = useRef(null);
    const deleteEvidencePopup = useRef(null);
    const queryClient = useQueryClient();
    const theme: any = useTheme();
    const queryFilter = useRef<QueryFilterRef>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [pageSize, setPageSize] = useState(50);
    // using any for now since multi select is off
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);
    const [flyoutData, setFlyoutData] = useState<Inquiry | null>(null);
    const [searchText, setSearchText] = useState(
      localStorage.getItem(`${stateStoreKey}:searchText`)
    );
    const [debouncedSearchText] = useDebounce(searchText, 500);

    const table = useTable();

    const [columnState, setColumnState] = useState(() => {
      return synchronizeColumnState(
        columnDefs,
        JSON.parse(localStorage.getItem(stateStoreKey) || "{}")
      );
    });

    let filterDimensions = InquiryFilterDimensions;

    const currentSort = useMemo(() => {
      let [sort] =
        columnState
          ?.filter((c) => c.sorting?.active)
          ?.map((c) => ({ field: c.dataField, sort: c.sorting?.direction })) ||
        [];

      return sort;
    }, [columnState]);

    const [query, setQuery] = useState<InquiryQuery | null>(null);

    const { data, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
      useInfiniteQuery({
        queryKey: [
          defaultQueryKey,
          {
            query: {
              ...query?.query,
              order: query?.query?.order || currentSort,
              pageSize,
            },
          },
        ],
        queryFn: ({ pageParam }) =>
          InquiriesAPI.query({
            query: {
              ...query?.query,
              order: query?.query?.order || currentSort,
              pageSize,
              page: pageParam,
            },
          }),
        getNextPageParam: (lastPage) => {
          return lastPage.nextPage;
        },
        getPreviousPageParam: (firstPage) => {
          if (firstPage.page - 1 === 0) return null;
          return firstPage.page - 1;
        },
        initialPageParam: 1,
        enabled: !!query,
        placeholderData: (data) => data,
      });

    const records = data?.pages?.reduce((acc, page) => {
      return [...acc, ...page.data];
    }, []);

    const totalRecords = data?.pages?.[0]?.total || 0;

    const debouncedFetchNextPage = useDebouncedCallback(() => {
      fetchNextPage();
    }, 50);

    const { data: customFields } = useQuery({
      queryKey: ["inquiries:customFields"],
      queryFn: () =>
        CustomFieldsApi.getCustomFields({
          category: CUSTOM_FIELD_CATEGORIES.INQUIRY,
        }),
    });

    // update filter dimensions with custom fields
    filterDimensions = useMemo(() => {
      if (customFields) {
        //update filter dimensions
        return [
          ...InquiryFilterDimensions,
          ...customFields?.map((field: CustomField) => ({
            name: field.field_name,
            field: `custom_field_${field.field_id}`,
            id: `custom_field_${field.field_id}`,
            type:
              field.editor_type === "dateBox"
                ? "date"
                : field.editor_type === "dropDown" ||
                  field.editor_type === "tagBox"
                ? "string"
                : "text",
            mode:
              field.editor_type === "dateBox"
                ? "date"
                : field.editor_type === "dropDown" ||
                  field.editor_type === "tagBox"
                ? "multi-select"
                : "text",
            options: field.options
              ? (typeof field.options === "string"
                  ? safeParseJSON(field.options as string)
                  : field.options
                ).map((o: string) => ({ option: o }))
              : null,
            selectionDisplayName: field.options ? "option" : null,
            selectionIdField: field.options ? "option" : null,
          })),
        ];
      }
      return InquiryFilterDimensions;
    }, [customFields]);

    const handleFilter = (newFilter: Condition) => {
      setQuery((q) => {
        return { query: { ...q?.query, ...newFilter, page: 1, pageSize } };
      });
    };
    const handleColumnVisibility = (
      column: ColumnProps<Inquiry>,
      visible: boolean
    ) => {
      setColumnState((cs) => {
        const currentColumn = cs.find((c) => c.dataField === column.dataField);
        if (currentColumn) {
          return cs.map((c) => {
            if (c.dataField === column.dataField) {
              return {
                ...c,
                visible,
              };
            }
            return c;
          });
        } else {
          return [
            ...cs,
            {
              ...column,
              visible,
            },
          ];
        }
      });
    };
    const handleExportTable = () => {
      // show snackbar
      enqueueSnackbar("Exporting inquiry table...", {
        variant: "info",
      });

      InquiriesAPI.exporInquiriesList({
        query: {
          ...query?.query,
          order: query?.query?.order || currentSort,
        },
        type: "xlsx",
        columns: columnState
          .filter((c) => c.visible !== false && c.showInColumnChooser !== false)
          .sort((a, b) => a.order - b.order)
          .map((c) => {
            return { dataField: c.dataField, header: c.caption, ...c };
          }),
        date_format: getDateFormat({ isMoment: true, includeTime: true }),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).then((res) => {
        const { signedUrl, filename } = res;
        const el = document.createElement("a");
        el.href = signedUrl.replace(
          "http://localhost:3000",
          "http://localhost:3001"
        );

        el.download = filename;
        el.click();

        // remove element
        el.remove();

        // remove snackbar
      });
    };
    const handleClearFilters = () => {
      queryFilter.current && queryFilter.current.clear();
    };
    const handleSort = (field: string) => {
      const savedColumn =
        columnState?.find((svc) => field === svc.dataField) || {};
      const order = !!savedColumn ? savedColumn?.sorting?.direction : null;

      let newOrder = null;

      // if sorted on a different column, reset the sort
      if (query?.query?.order?.field !== field) {
        newOrder = {
          field,
          sort: "asc",
        };
      }

      // otherwise rotate the sort options on the current column
      else {
        switch (order) {
          case "asc":
            newOrder = {
              field,
              sort: "desc",
            };
            break;
          case "desc":
            newOrder = null;
            break;
          default:
            newOrder = {
              field,
              sort: "asc",
            };
        }
      }

      let newCols = columnState?.map((c) => {
        if (c.dataField === field) {
          return {
            ...c,
            sorting: {
              active: newOrder ? true : false,
              direction: newOrder?.sort,
            },
          };
        }
        delete c.sorting;
        return c;
      });

      let newQuery = {
        query: {
          ...query?.query,
          order: newOrder,
        },
      };

      setColumnState(newCols);

      setQuery(newQuery);
    };
    const handleColumnReorder = (
      newOrder: { column: string; order: number }[]
    ) => {
      setColumnState((cs) => {
        return newOrder.map((o) => {
          return {
            ...cs.find((c) => c.dataField === o.column),
            order: o.order,
          };
        });
      });
    };
    const handleColumnResize = (e: ColumnResize) => {
      setColumnState((cs) => {
        return cs.map((c) => {
          const col = e.columns.find((col) => col.dataField === c.dataField);
          if (col) {
            return {
              ...c,
              width: parseInt(col.width.replace(/px/g, "")),
            };
          }
          return c;
        });
      });
    };
    const handleActionButtonClick = (rowData: Inquiry) => {
      setIsFlyoutVisible(true);
      setFlyoutData(rowData);
    };
    const handleGetNextItem = (id: number) => {
      const current = records?.findIndex((i: Inquiry) => i.inquiry_id === id);
      const nextItem = records[current + 1] || records[0];

      setFlyoutData(nextItem);
    };
    const handleGetPrevItem = (id: number) => {
      const current = records?.findIndex((i: Inquiry) => i.inquiry_id === id);
      const prevItem = records[current - 1] || records[records.length - 1];

      setFlyoutData(prevItem);
    };
    const handleRowSelection = (rows: any[]) => {
      setSelectedRows(rows);
    };
    const onInquiryDeleted = (deletedItem: Inquiry) => {
      setIsFlyoutVisible(false);
      setFlyoutData(null);
      queryClient.setQueryData(
        [
          defaultQueryKey,
          {
            query: {
              ...query?.query,
              order: query?.query?.order || currentSort,
              pageSize,
            },
          },
        ],
        (data: PageData<Inquiry>) => {
          if (!data) return null;
          const newPages = data?.pages.map((page) => {
            page.data =
              page?.data?.filter((item) => item.uuid !== deletedItem.uuid) ||
              page.data;
            return page;
          });

          return {
            ...data,
            pages: newPages,
          };
        }
      );
    };
    const handleScroll = (e: React.MouseEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const pageLength = data?.pages?.length ? data?.pages?.length : 0;
      if (scrollHeight - scrollTop <= clientHeight + 100 * pageLength) {
        if (hasNextPage && !isFetchingNextPage) {
          debouncedFetchNextPage();
        }
      }
    };

    useEffect(() => {
      const localStorageItem = localStorage.getItem(stateStoreKey);
      let oldState = localStorageItem ? JSON.parse(localStorageItem) : {};
      localStorage.setItem(
        stateStoreKey,
        JSON.stringify({
          ...oldState,
          cols: columnState,
        })
      );
    }, [columnState]);

    useEffect(() => {
      if (!customFields) return;
      // Add custom fields to column defs
      setColumnState((cs) => {
        return addCustomFieldsToColumnDefs(customFields, cs);
      });
    }, [customFields]);

    const { queryButton, conditions } = useQueryFilter({
      dimensions: filterDimensions.sort((a, b) => a.name.localeCompare(b.name)),
      onQuerySet: handleFilter,
      queryFilter: queryFilter,
      stateStoring: {
        enabled: true,
        type: "localStorage",
        storageKey: stateStoreKey,
      },
    });

    useEffect(() => {
      setQuery((q) => ({
        query: {
          ...q?.query,
          search: debouncedSearchText ? debouncedSearchText : null,
          page: 1,
        },
      }));
    }, [debouncedSearchText]);

    return (
      <div className={className}>
        <Helmet title="Inquiries" />
        <Typography variant="h3" gutterBottom display="inline">
          Inquiries
        </Typography>
        <div
          style={{
            display: "flex",
            flex: "initial",
            flexDirection: "row",
            alignContent: "center",
            alignItems: "center",
            marginBottom: 10,
            marginTop: 10,
          }}
        >
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={!hasPermission(MONOLITH_PERMISSIONS.INQUIRIES_CREATE)}
            onClick={(e) => {
              setShowCreateModal(true);
            }}
            style={{
              minWidth: "fit-content",
              fontSize: 11,
              padding: "3px 6px",
            }}
          >
            + New Inquiry
          </Button>
          <div style={{ marginLeft: 10 }}>{queryButton}</div>
          <ItemTotalBadge total={totalRecords || 0} Icon={SendIcon} />
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignContent: "center",
              alignItems: "center",
              minWidth: "fit-content",
            }}
          >
            <ComboButton
              type="multi-select"
              data={columnState.filter((c) => c.showInColumnChooser !== false)}
              displayField="caption"
              idField={"dataField"}
              selectedItems={columnState.filter((c) => c.visible !== false)}
              variant="outlined"
              closeOnSelect={false}
              showSearch={true}
              dropDownTitle={() => {
                return (
                  <div
                    style={{
                      margin: "5px 0px",
                      padding: 3,
                      color: theme.palette.text.secondary,
                      display: "flex",
                      alignItems: "center",
                      minWidth: 200,
                    }}
                  >
                    Select Columns
                  </div>
                );
              }}
              onItemDeSelect={(item) => {
                handleColumnVisibility(item, false);
              }}
              onItemSelect={(item) => {
                handleColumnVisibility(item, true);
              }}
              textColor={theme.palette.text.secondary}
              title={"Select Columns"}
            >
              <ViewColumnOutlinedIcon style={{ fontSize: 18 }} />
            </ComboButton>
            <ComboButton
              data={undefined}
              selectedItems={undefined}
              type="button"
              variant="outlined"
              textColor={theme.palette.text.secondary}
              title={"Export Table"}
              onClick={handleExportTable}
            >
              <FileDownloadOutlinedIcon style={{ fontSize: 18 }} />
            </ComboButton>
            <ComboButton
              data={undefined}
              selectedItems={undefined}
              type="button"
              variant="outlined"
              textColor={theme.palette.text.secondary}
              title={"Clear Filters"}
              onClick={handleClearFilters}
            >
              <FilterListOffIcon style={{ fontSize: 18 }} />
            </ComboButton>
            <ComboButton
              data={undefined}
              selectedItems={undefined}
              type="button"
              variant="outlined"
              textColor={theme.palette.text.secondary}
              title={table.isCompact ? "Zoom In" : "Zoom Out"}
              onClick={() => table.toggleCompact()}
            >
              {table.isCompact && (
                <ZoomOutMapOutlinedIcon style={{ fontSize: 18 }} />
              )}
              {!table.isCompact && (
                <ZoomInMapOutlinedIcon style={{ fontSize: 18 }} />
              )}
            </ComboButton>
            <ComboButton
              data={undefined}
              selectedItems={undefined}
              type="button"
              variant={"outlined"}
              textColor={
                table.isStriped
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary
              }
              title={table.isStriped ? "Hide Stripes" : "Show Stripes"}
              onClick={() => table.toggleStripes()}
            >
              <ReorderOutlinedIcon style={{ fontSize: 18 }} />
            </ComboButton>
            <Input
              placeholder="Search Inquiries"
              variant="outlined"
              height={30}
              defaultValue={searchText}
              onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                const currentValue = e.currentTarget.value.trim();
                setSearchText(currentValue);

                localStorage.setItem(
                  `${stateStoreKey}:searchText`,
                  currentValue
                );
              }}
              style={{
                marginLeft: "10px",
                border: searchText
                  ? `1px solid ${theme.palette.primary.main}`
                  : "",
              }}
            />
          </div>
        </div>
        {data && customFields && (
          <>
            {<div>{conditions}</div>}
            <Table
              data={records || []}
              totalRecords={totalRecords}
              keyValue="inquiry_id"
              tableInstance={table}
              columnProps={{ minWidth: 150, width: 150 }}
              onHeaderClick={(col: TableColumn<Inquiry>) =>
                col?.sorting?.enabled === false
                  ? null
                  : handleSort(col.dataField)
              }
              onScroll={handleScroll}
              onColumnReorder={handleColumnReorder}
              onColumnResize={handleColumnResize}
              onActionButtonClick={handleActionButtonClick}
              onSelectionChanged={handleRowSelection}
              selectedRows={selectedRows}
              showActionColumn={true}
              focusedRow={flyoutData}
            >
              {columnState.map((col) => {
                return <Column key={col.dataField} {...col} />;
              })}
            </Table>
            {isFlyoutVisible && flyoutData && (
              <Flyout
                onClose={() => {
                  setIsFlyoutVisible(false);
                  setFlyoutData(null);
                }}
              >
                <FlyoutHeader>
                  <div style={{ width: "100%" }}>
                    <h4
                      style={{
                        margin: "5px 0px",
                        color: theme.palette.text.primary,
                      }}
                    >
                      {flyoutData?.request_name ||
                        `Inquiry #${flyoutData?.inquiry_id}`}
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        alignContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          color: theme.palette.text.secondary,
                          fontSize: 12,
                        }}
                      >
                        Inquiry Details
                      </div>
                      <div
                        style={{
                          marginLeft: "auto",
                          display: "flex",
                          alignContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <TaskButton
                          onClick={() =>
                            handleGetPrevItem(flyoutData?.inquiry_id ?? 0)
                          }
                          style={{
                            fontSize: 10,
                          }}
                          variant="outlined"
                        >
                          <KeyboardArrowLeftOutlinedIcon
                            style={{ fontSize: 17 }}
                          />
                        </TaskButton>
                        <TaskButton
                          onClick={() =>
                            handleGetNextItem(flyoutData?.inquiry_id ?? 0)
                          }
                          style={{
                            fontSize: 10,
                          }}
                          variant="outlined"
                        >
                          <KeyboardArrowRightOutlinedIcon
                            style={{ fontSize: 17 }}
                          />
                        </TaskButton>
                      </div>
                    </div>
                  </div>
                </FlyoutHeader>
                <FlyoutContent
                  defaultInfo={flyoutData}
                  editEvidencePopup={editEvidencePopup}
                  deleteEvidencePopup={deleteEvidencePopup}
                  onDelete={onInquiryDeleted}
                  setIsFlyoutVisible={setIsFlyoutVisible}
                />
              </Flyout>
            )}
          </>
        )}
        {!(data && customFields) && (
          <Loader message={"Retrieving Inquiries..."} />
        )}
        <CreateInquiryModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={(result) => {
            const created = result as Inquiry | undefined;
            if (created?.inquiry_id) {
              navigate(`/inquiries/${created.inquiry_id}`);
            }
          }}
        />
      </div>
    );
  }
)`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const InquirySection = () => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();

  if (!hasPermission(MONOLITH_PERMISSIONS.INQUIRIES_READ_ALL))
    return <AccessDeniedMessage />;

  return <InquirySectionWrapper />;
};

export default InquirySection;
